import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Users, UserCheck,
    RefreshCw, Shield, X,
    FileText, Upload, Settings, ClipboardList, CheckCircle,
    Activity, Eye, ToggleRight,
    Search, Plus, Archive, MessageSquare, ChevronDown, ChevronUp, Save, Trash2, UserPlus
} from 'lucide-react';
import api, { officialDocuments, settings as settingsApi, isDistrictAdmin, getUserRole, betaFeedback, feedbackAdmins } from '../services/api';

const TAB_MODULE_MAP = {
    documents: 'documents',
    settings: 'settings',
    team: 'settings',
    audit: 'audit',
    feedback: 'feedback',
};

const AdminDashboard = ({ onClose, adminKey, onNavigateToFeedback }) => {
    const [activeTab, setActiveTab] = useState(null);
    const [allowedModules, setAllowedModules] = useState(null); // null = loading
    const userRole = getUserRole();

    const allTabs = [
        { id: 'documents', label: 'Documents', icon: FileText },
        { id: 'settings', label: 'Settings', icon: Settings },
        { id: 'team', label: 'Team Access', icon: UserPlus },
        { id: 'audit', label: 'Audit Log', icon: ClipboardList },
        { id: 'feedback', label: 'Feedback', icon: MessageSquare },
    ];

    // Fetch user's allowed modules on mount
    useEffect(() => {
        (async () => {
            try {
                const res = await feedbackAdmins.myModules();
                const mods = res.data?.modules || [];
                setAllowedModules(mods);
                // Default to first allowed tab
                const firstAllowed = allTabs.find(t => mods.includes(TAB_MODULE_MAP[t.id]));
                setActiveTab(firstAllowed?.id || 'documents');
            } catch (err) {
                console.error('Failed to load modules:', err);
                // Fallback: show all tabs
                setAllowedModules(['feedback', 'documents', 'settings', 'audit']);
                setActiveTab('documents');
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Filter tabs based on modules
    const tabs = allowedModules
        ? allTabs.filter(t => allowedModules.includes(TAB_MODULE_MAP[t.id]))
        : allTabs;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-slate-50 rounded-3xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl"
            >
                {/* Header */}
                <div className="bg-slate-900 text-white px-8 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                            <Shield size={22} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">Campus Administration</h2>
                            <p className="text-slate-400 text-sm">
                                {userRole === 'district_admin' ? 'District Admin' : 'Campus Admin'} · Lamar CISD
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Tab Nav */}
                <div className="bg-white border-b border-slate-200 px-8 flex gap-1">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => {
                                if (tab.id === 'feedback' && onNavigateToFeedback) {
                                    onNavigateToFeedback();
                                } else {
                                    setActiveTab(tab.id);
                                }
                            }}
                            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                                ? 'border-amber-500 text-amber-600'
                                : 'border-transparent text-slate-500 hover:text-slate-700'
                                }`}
                        >
                            <tab.icon size={16} />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div className="p-8 overflow-y-auto max-h-[calc(90vh-140px)]">
                    {allowedModules === null ? (
                        <div className="flex justify-center py-16">
                            <RefreshCw size={24} className="animate-spin text-slate-400" />
                        </div>
                    ) : (
                        <AnimatePresence mode="wait">
                            {activeTab === 'documents' && <DocumentsTab key="docs" adminKey={adminKey} />}
                            {activeTab === 'settings' && <SettingsTab key="settings" />}
                            {activeTab === 'team' && <TeamAccessTab key="team" />}
                            {activeTab === 'audit' && <AuditTab key="audit" adminKey={adminKey} />}
                            {activeTab === 'feedback' && <FeedbackTab key="feedback" />}
                        </AnimatePresence>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};


// ============ DOCUMENTS TAB ============

const DocumentsTab = () => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUpload, setShowUpload] = useState(false);
    const [filter, setFilter] = useState('all');

    const fetchDocuments = useCallback(async () => {
        setLoading(true);
        try {
            const res = await officialDocuments.list();
            setDocuments(res.data.documents || []);
        } catch (err) {
            console.error('Failed to fetch documents:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

    const handleActivate = async (id) => {
        try {
            await officialDocuments.activate(id);
            fetchDocuments();
        } catch (err) {
            console.error('Activate failed:', err);
        }
    };

    const handleRetire = async (id) => {
        try {
            await officialDocuments.retire(id);
            fetchDocuments();
        } catch (err) {
            console.error('Retire failed:', err);
        }
    };

    const docTypeLabels = {
        student_handbook: 'Student Handbook',
        academic_calendar: 'Academic Calendar',
        lesson_plan_template: 'Lesson Plan Template',
        bell_schedule: 'Bell Schedule',
        testing_dates: 'Testing Dates',
        campus_procedures: 'Campus Procedures',
        emergency_procedures: 'Emergency Procedures'
    };

    const filtered = filter === 'all'
        ? documents
        : documents.filter(d => d.doc_type === filter);

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            {/* Header Row */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-slate-900">Official Documents</h3>
                    <p className="text-sm text-slate-500">Upload and manage district/campus documents</p>
                </div>
                <button
                    onClick={() => setShowUpload(!showUpload)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors font-medium text-sm"
                >
                    <Upload size={16} />
                    Upload Document
                </button>
            </div>

            {/* Upload Panel */}
            <AnimatePresence>
                {showUpload && (
                    <UploadPanel
                        onClose={() => setShowUpload(false)}
                        onSuccess={() => { setShowUpload(false); fetchDocuments(); }}
                    />
                )}
            </AnimatePresence>

            {/* Filter Bar */}
            <div className="flex gap-2 mb-4 flex-wrap">
                <FilterChip label="All" active={filter === 'all'} onClick={() => setFilter('all')} />
                {Object.entries(docTypeLabels).map(([key, label]) => (
                    <FilterChip key={key} label={label} active={filter === key} onClick={() => setFilter(key)} />
                ))}
            </div>

            {/* Document List */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <RefreshCw size={24} className="animate-spin text-slate-400" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
                    <FileText size={40} className="mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No documents uploaded yet</p>
                    <p className="text-sm text-slate-400 mt-1">Upload your first document to get started</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {filtered.map(doc => (
                        <DocumentRow
                            key={doc.id}
                            doc={doc}
                            typeLabel={docTypeLabels[doc.doc_type] || doc.doc_type}
                            onActivate={() => handleActivate(doc.id)}
                            onRetire={() => handleRetire(doc.id)}
                        />
                    ))}
                </div>
            )}
        </motion.div>
    );
};

const FilterChip = ({ label, active, onClick }) => (
    <button
        onClick={onClick}
        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${active
            ? 'bg-amber-100 text-amber-700 border border-amber-200'
            : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
            }`}
    >
        {label}
    </button>
);

const DocumentRow = ({ doc, typeLabel, onActivate, onRetire }) => (
    <div className={`bg-white rounded-xl p-4 border transition-colors ${doc.active ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-100'
        }`}>
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${doc.active ? 'bg-emerald-100' : 'bg-slate-100'
                    }`}>
                    <FileText size={18} className={doc.active ? 'text-emerald-600' : 'text-slate-400'} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900 truncate">{doc.title}</p>
                        {doc.active && (
                            <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                                <CheckCircle size={10} /> Active
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-slate-500">{typeLabel}</span>
                        <span className="text-xs text-slate-400">v{doc.version}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${doc.scope === 'district'
                            ? 'bg-blue-50 text-blue-600'
                            : 'bg-purple-50 text-purple-600'
                            }`}>
                            {doc.scope}
                        </span>
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 ml-4">
                {!doc.active ? (
                    <button
                        onClick={onActivate}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
                    >
                        <ToggleRight size={14} /> Activate
                    </button>
                ) : (
                    <button
                        onClick={onRetire}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-500 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                        <Archive size={14} /> Retire
                    </button>
                )}
            </div>
        </div>
    </div>
);

const UploadPanel = ({ onClose, onSuccess }) => {
    const [title, setTitle] = useState('');
    const [docType, setDocType] = useState('student_handbook');
    const [scope, setScope] = useState('campus');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const handleUpload = async () => {
        if (!file || !title) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('title', title);
            formData.append('doc_type', docType);
            formData.append('scope', scope);
            await officialDocuments.upload(formData);
            onSuccess();
        } catch (err) {
            console.error('Upload failed:', err);
            alert('Upload failed: ' + (err.response?.data?.detail || err.message));
        } finally {
            setUploading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-6"
        >
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-slate-900">Upload New Document</h4>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <X size={18} />
                    </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm text-slate-600 mb-1.5">Title</label>
                        <input
                            type="text"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            placeholder="e.g., Student Handbook 2025-2026"
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-slate-600 mb-1.5">Document Type</label>
                        <select
                            value={docType}
                            onChange={e => setDocType(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
                        >
                            <option value="student_handbook">Student Handbook</option>
                            <option value="academic_calendar">Academic Calendar</option>
                            <option value="lesson_plan_template">Lesson Plan Template</option>
                            <option value="bell_schedule">Bell Schedule</option>
                            <option value="testing_dates">Testing Dates</option>
                            <option value="campus_procedures">Campus Procedures</option>
                            <option value="emergency_procedures">Emergency Procedures</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm text-slate-600 mb-1.5">Scope</label>
                        <select
                            value={scope}
                            onChange={e => setScope(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
                        >
                            <option value="campus">Campus</option>
                            {isDistrictAdmin() && <option value="district">District</option>}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm text-slate-600 mb-1.5">File</label>
                        <div
                            onClick={() => fileRef.current?.click()}
                            className="w-full px-3 py-2 rounded-lg border border-dashed border-slate-300 text-sm text-slate-500 cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors"
                        >
                            {file ? file.name : 'Click to select file…'}
                        </div>
                        <input
                            ref={fileRef}
                            type="file"
                            onChange={e => setFile(e.target.files[0])}
                            className="hidden"
                            accept=".pdf,.docx,.doc,.txt"
                        />
                    </div>
                </div>
                <div className="flex justify-end mt-4 gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700">
                        Cancel
                    </button>
                    <button
                        onClick={handleUpload}
                        disabled={!file || !title || uploading}
                        className="flex items-center gap-2 px-5 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                    >
                        {uploading ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
                        {uploading ? 'Uploading…' : 'Upload'}
                    </button>
                </div>
            </div>
        </motion.div>
    );
};


// ============ SETTINGS TAB ============

const SettingsTab = () => {
    const [allSettings, setAllSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [editKey, setEditKey] = useState(null);
    const [editValue, setEditValue] = useState('');

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await settingsApi.getAll();
                setAllSettings(res.data.settings || {});
            } catch (err) {
                console.error('Failed to load settings:', err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const handleSave = async (key) => {
        try {
            const parsed = JSON.parse(editValue);
            await settingsApi.update(key, parsed, 'campus');
            const res = await settingsApi.getAll();
            setAllSettings(res.data.settings || {});
            setEditKey(null);
        } catch (err) {
            alert('Invalid JSON or save failed: ' + err.message);
        }
    };

    const settingDescriptions = {
        bell_schedule: 'Period times and schedule types',
        lesson_plan_format: 'Default lesson plan format and required sections',
        assessment_windows: 'Testing dates and blocked instructional periods',
        instructional_priorities: 'Campus or district focus areas for lesson generation',
        grading_periods: 'Six-week grading period date ranges',
    };

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900">Configuration Settings</h3>
                <p className="text-sm text-slate-500">Campus settings override district defaults</p>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <RefreshCw size={24} className="animate-spin text-slate-400" />
                </div>
            ) : Object.keys(allSettings).length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
                    <Settings size={40} className="mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No settings configured yet</p>
                    <p className="text-sm text-slate-400 mt-1">Settings will appear here when configured by an admin</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {Object.entries(allSettings).map(([key, data]) => (
                        <div key={key} className="bg-white rounded-xl p-4 border border-slate-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-slate-900">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                                    <div className="flex items-center gap-3 mt-0.5">
                                        <span className="text-xs text-slate-400">
                                            {settingDescriptions[key] || 'Custom setting'}
                                        </span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${data.scope === 'district' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'
                                            }`}>
                                            {data.scope} · v{data.version}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setEditKey(editKey === key ? null : key);
                                        setEditValue(JSON.stringify(data.value, null, 2));
                                    }}
                                    className="px-3 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors"
                                >
                                    {editKey === key ? 'Close' : 'Edit'}
                                </button>
                            </div>
                            {editKey === key && (
                                <div className="mt-3 pt-3 border-t border-slate-100">
                                    <textarea
                                        value={editValue}
                                        onChange={e => setEditValue(e.target.value)}
                                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-300 min-h-[120px]"
                                    />
                                    <div className="flex justify-end mt-2 gap-2">
                                        <button onClick={() => setEditKey(null)} className="px-3 py-1.5 text-sm text-slate-500">Cancel</button>
                                        <button
                                            onClick={() => handleSave(key)}
                                            className="px-4 py-1.5 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </motion.div>
    );
};


// ============ USERS TAB ============

const ALL_MODULES = ['feedback', 'documents', 'settings', 'audit'];
const MODULE_LABELS = { feedback: 'Feedback', documents: 'Documents', settings: 'Settings', audit: 'Audit' };
const MODULE_COLORS = {
    feedback: 'bg-amber-100 text-amber-700',
    documents: 'bg-blue-100 text-blue-700',
    settings: 'bg-violet-100 text-violet-700',
    audit: 'bg-slate-100 text-slate-600',
};

const TeamAccessTab = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newEmail, setNewEmail] = useState('');
    const [newModules, setNewModules] = useState([...ALL_MODULES]);
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchAdmins = useCallback(async () => {
        setLoading(true);
        try {
            const res = await feedbackAdmins.list();
            setAdmins(res.data.admins || []);
        } catch (err) {
            console.error('Failed to load admins:', err);
            setError('Failed to load team access list');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

    const toggleModule = (mod) => {
        setNewModules(prev =>
            prev.includes(mod) ? prev.filter(m => m !== mod) : [...prev, mod]
        );
    };

    const handleAdd = async () => {
        if (!newEmail.trim() || !newEmail.includes('@')) {
            setError('Please enter a valid email address');
            return;
        }
        if (newModules.length === 0) {
            setError('Please select at least one module permission');
            return;
        }
        setAdding(true);
        setError('');
        setSuccess('');
        try {
            await feedbackAdmins.add(newEmail.trim().toLowerCase(), newModules);
            setNewEmail('');
            setNewModules([...ALL_MODULES]);
            setSuccess(`Added ${newEmail.trim().toLowerCase()}`);
            fetchAdmins();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            const msg = err.response?.data?.detail || 'Failed to add admin';
            setError(msg);
        } finally {
            setAdding(false);
        }
    };

    const handleRemove = async (email) => {
        if (!window.confirm(`Remove feedback admin access for ${email}?`)) return;
        setError('');
        setSuccess('');
        try {
            await feedbackAdmins.remove(email);
            setSuccess(`Removed ${email}`);
            fetchAdmins();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            const msg = err.response?.data?.detail || 'Failed to remove admin';
            setError(msg);
        }
    };

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-slate-900">Team Access</h3>
                    <p className="text-sm text-slate-500">Manage who can access admin modules and triage feedback</p>
                </div>
            </div>

            {/* Add admin form */}
            <div className="mb-4 bg-white rounded-xl border border-slate-100 p-4">
                <div className="flex gap-2 mb-3">
                    <input
                        type="email"
                        value={newEmail}
                        onChange={e => setNewEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAdd()}
                        placeholder="teammate@school.org"
                        className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                    />
                    <button
                        onClick={handleAdd}
                        disabled={adding}
                        className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors font-medium text-sm disabled:opacity-50"
                    >
                        {adding ? <RefreshCw size={14} className="animate-spin" /> : <UserPlus size={14} />}
                        Add
                    </button>
                </div>
                {/* Module permission checkboxes */}
                <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-slate-400">Module access:</span>
                    {ALL_MODULES.map(mod => (
                        <label key={mod} className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={newModules.includes(mod)}
                                onChange={() => toggleModule(mod)}
                                className="rounded border-slate-300 text-amber-500 focus:ring-amber-300"
                            />
                            <span className="text-slate-600">{MODULE_LABELS[mod]}</span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Messages */}
            {error && (
                <div className="mb-4 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    {error}
                </div>
            )}
            {success && (
                <div className="mb-4 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
                    {success}
                </div>
            )}

            {/* Admin list */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <RefreshCw size={24} className="animate-spin text-slate-400" />
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="text-left px-4 py-3 text-slate-500 font-medium">Email</th>
                                <th className="text-left px-4 py-3 text-slate-500 font-medium">Modules</th>
                                <th className="text-left px-4 py-3 text-slate-500 font-medium">Source</th>
                                <th className="text-left px-4 py-3 text-slate-500 font-medium">Added</th>
                                <th className="text-right px-4 py-3 text-slate-500 font-medium"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {admins.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center py-12 text-slate-400">
                                        No feedback admins configured
                                    </td>
                                </tr>
                            ) : (
                                admins.map((admin, idx) => (
                                    <tr key={admin.email + idx} className="border-b border-slate-50 hover:bg-slate-50/50">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 bg-amber-50 rounded-full flex items-center justify-center">
                                                    <Shield size={14} className="text-amber-600" />
                                                </div>
                                                <span className="font-medium text-slate-700">{admin.email}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex gap-1 flex-wrap">
                                                {(admin.modules || ALL_MODULES).map(mod => (
                                                    <span
                                                        key={mod}
                                                        className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${MODULE_COLORS[mod] || 'bg-slate-100 text-slate-600'}`}
                                                    >
                                                        {MODULE_LABELS[mod] || mod}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                admin.source === 'env' ? 'bg-slate-100 text-slate-600' :
                                                admin.source === 'env+database' ? 'bg-blue-50 text-blue-600' :
                                                'bg-emerald-50 text-emerald-600'
                                            }`}>
                                                {admin.source === 'env' ? 'System' :
                                                 admin.source === 'env+database' ? 'System + DB' : 'Database'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-500">
                                            {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {admin.source === 'database' && (
                                                <button
                                                    onClick={() => handleRemove(admin.email)}
                                                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Remove access"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <p className="text-xs text-slate-400 mt-4">
                System admins are configured via the FEEDBACK_ADMIN_EMAILS environment variable and cannot be removed here.
                Database admins are managed through this interface with granular module permissions.
            </p>
        </motion.div>
    );
};



// ============ AUDIT TAB ============

const AuditTab = ({ adminKey }) => {
    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await api.get('/admin/analytics', {
                    headers: { 'X-Admin-Key': adminKey }
                });
                setAuditLogs(res.data.recent_activity || []);
            } catch (err) {
                console.error('Failed to load audit log:', err);
            } finally {
                setLoading(false);
            }
        })();
    }, [adminKey]);

    const actionColors = {
        upload_document: 'bg-blue-100 text-blue-600',
        update_setting: 'bg-amber-100 text-amber-600',
        activate_document_version: 'bg-emerald-100 text-emerald-600',
        retire_document: 'bg-slate-100 text-slate-600',
    };

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900">Audit Log</h3>
                <p className="text-sm text-slate-500">Track all administrative actions</p>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <RefreshCw size={24} className="animate-spin text-slate-400" />
                </div>
            ) : auditLogs.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
                    <ClipboardList size={40} className="mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No audit entries yet</p>
                    <p className="text-sm text-slate-400 mt-1">Actions will be logged here after migration</p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-50">
                    {auditLogs.map((entry, idx) => (
                        <div key={idx} className="px-4 py-3 flex items-center gap-3">
                            <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <Activity size={14} className="text-slate-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-slate-700">
                                    <span className="font-medium">{entry.user_codename || entry.user_email || 'System'}</span>
                                    {' — '}{entry.details || entry.action || 'Action performed'}
                                </p>
                                <p className="text-xs text-slate-400 mt-0.5">
                                    {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : entry.created_at || '—'}
                                </p>
                            </div>
                            {entry.action && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${actionColors[entry.action] || 'bg-slate-100 text-slate-600'}`}>
                                    {(entry.action || '').replace(/_/g, ' ')}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </motion.div>
    );
};


// ============ FEEDBACK TAB ============

const CATEGORY_META = {
    bug:             { label: 'Bug',             emoji: '🔴', color: 'bg-red-100 text-red-700 border-red-200' },
    improvement:     { label: 'Improvement',     emoji: '🟡', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    feature_request: { label: 'Feature Request', emoji: '🟢', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const STATUS_META = {
    open:        { label: 'Open',        color: 'bg-blue-100 text-blue-700' },
    in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
    resolved:    { label: 'Resolved',    color: 'bg-emerald-100 text-emerald-700' },
};

const FeedbackTab = () => {
    const [subTab, setSubTab] = useState('categorized');
    const [items, setItems] = useState([]);
    const [weeklyItems, setWeeklyItems] = useState([]);
    const [updateItems, setUpdateItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [catFilter, setCatFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedId, setExpandedId] = useState(null);
    const [saving, setSaving] = useState(null);
    const [saveResult, setSaveResult] = useState(null); // { id, success, errors[] }
    // Mutable edit state per item
    const [editStatus, setEditStatus] = useState({});
    const [editNotes, setEditNotes] = useState({});
    const [editReply, setEditReply] = useState({});
    const [editSendEmail, setEditSendEmail] = useState({});

    const fetchFeedback = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await betaFeedback.listCategorized();
            setItems(res.data?.submissions || []);
        } catch (err) {
            console.error('Failed to load feedback:', err);
            setError(err.response?.data?.detail || 'Failed to load feedback. You may not have admin access.');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchWeekly = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await betaFeedback.listWeekly();
            setWeeklyItems(res.data?.submissions || []);
        } catch (err) {
            console.error('Failed to load weekly feedback:', err);
            setError(err.response?.data?.detail || 'Failed to load weekly feedback.');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchUpdates = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await betaFeedback.listUpdates();
            setUpdateItems(res.data?.submissions || []);
        } catch (err) {
            console.error('Failed to load update feedback:', err);
            setError(err.response?.data?.detail || 'Failed to load update feedback.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (subTab === 'categorized') fetchFeedback();
        else if (subTab === 'weekly') fetchWeekly();
        else if (subTab === 'updates') fetchUpdates();
    }, [subTab, fetchFeedback, fetchWeekly, fetchUpdates]);

    // ── Single unified save ──────────────────────────────────────────
    const handleSave = async (id) => {
        setSaving(id);
        setSaveResult(null);
        const errors = [];

        // 1. PATCH status/notes if changed
        const patchData = {};
        if (editStatus[id] !== undefined) patchData.status = editStatus[id];
        if (editNotes[id] !== undefined) patchData.internal_notes = editNotes[id];

        if (Object.keys(patchData).length > 0) {
            try {
                await betaFeedback.patchCategorized(id, patchData);
            } catch (err) {
                console.error('PATCH failed:', err);
                errors.push('Failed to save status/notes');
            }
        }

        // 2. POST /reply if reply text exists
        let replyResult = null;
        const replyText = (editReply[id] || '').trim();
        if (replyText) {
            try {
                const res = await betaFeedback.replyCategorized(id, {
                    admin_reply: replyText,
                    send_email: !!editSendEmail[id],
                });
                replyResult = res.data;
                if (replyResult.email_requested && !replyResult.email_sent) {
                    errors.push(`Email failed: ${replyResult.email_error || 'unknown error'}`);
                }
            } catch (err) {
                console.error('Reply failed:', err);
                errors.push('Failed to save admin reply');
            }
        }

        // Update local state optimistically
        setItems(prev => prev.map(item => {
            if (item.id !== id) return item;
            const updated = { ...item };
            if (patchData.status) updated.status = patchData.status;
            if (patchData.status === 'resolved') updated.resolved_at = new Date().toISOString();
            if (patchData.internal_notes !== undefined) updated.internal_notes = patchData.internal_notes;
            if (replyResult?.reply_saved) {
                updated.admin_reply = replyText;
                updated.admin_reply_at = replyResult.replied_at;
                updated.replied_by = replyResult.replied_by;
                if (replyResult.email_sent) {
                    updated.email_reply_sent = true;
                    updated.email_reply_sent_at = replyResult.replied_at;
                }
            }
            return updated;
        }));

        setSaveResult({
            id,
            success: errors.length === 0,
            errors,
            emailSent: replyResult?.email_sent || false,
        });
        setSaving(null);

        // Clear result after 4s
        setTimeout(() => setSaveResult(null), 4000);
    };

    const toggleExpand = (id, item) => {
        if (expandedId === id) {
            setExpandedId(null);
        } else {
            setExpandedId(id);
            // Init edit state from current item
            setEditStatus(prev => ({ ...prev, [id]: item.status || 'open' }));
            setEditNotes(prev => ({ ...prev, [id]: item.internal_notes || '' }));
            setEditReply(prev => ({ ...prev, [id]: item.admin_reply || '' }));
            setEditSendEmail(prev => ({ ...prev, [id]: false }));
        }
    };

    // Apply filters
    const filtered = items.filter(i => {
        if (catFilter !== 'all' && i.category !== catFilter) return false;
        if (statusFilter !== 'all' && i.status !== statusFilter) return false;
        return true;
    });

    const counts = {
        total: items.length,
        open: items.filter(i => i.status === 'open').length,
        in_progress: items.filter(i => i.status === 'in_progress').length,
        resolved: items.filter(i => i.status === 'resolved').length,
    };

    const subTabs = [
        { id: 'categorized', label: 'Categorized', count: items.length },
        { id: 'weekly', label: 'Weekly Pulse', count: weeklyItems.length },
        { id: 'updates', label: 'Update Reports', count: updateItems.length },
    ];

    const refreshCurrent = () => {
        if (subTab === 'categorized') fetchFeedback();
        else if (subTab === 'weekly') fetchWeekly();
        else if (subTab === 'updates') fetchUpdates();
    };

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-slate-900">Beta Feedback</h3>
                    <p className="text-sm text-slate-500">
                        {subTab === 'categorized'
                            ? `${counts.total} total · ${counts.open} open · ${counts.in_progress} in progress · ${counts.resolved} resolved`
                            : subTab === 'weekly'
                                ? `${weeklyItems.length} weekly submissions`
                                : `${updateItems.length} update reports`
                        }
                    </p>
                </div>
                <button
                    onClick={refreshCurrent}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                    <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                    Refresh
                </button>
            </div>

            {/* Sub-tab navigation */}
            <div className="flex gap-1 mb-4 p-1 bg-slate-100 rounded-xl">
                {subTabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setSubTab(tab.id)}
                        className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all ${
                            subTab === tab.id
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        {tab.label}
                        {tab.count > 0 && (
                            <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                                subTab === tab.id ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-500'
                            }`}>
                                {tab.count}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Error */}
            {error && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                    <Activity size={16} /> {error}
                </div>
            )}

            {/* Loading */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <RefreshCw size={24} className="animate-spin text-slate-400" />
                </div>
            ) : (
                <>
                    {/* ── CATEGORIZED SUB-TAB ── */}
                    {subTab === 'categorized' && (
                        <>
                            {/* Filters */}
                            <div className="flex gap-2 mb-2 flex-wrap">
                                <span className="text-xs text-slate-400 self-center mr-1">Category:</span>
                                <FilterChip label="All" active={catFilter === 'all'} onClick={() => setCatFilter('all')} />
                                <FilterChip label="🔴 Bugs" active={catFilter === 'bug'} onClick={() => setCatFilter('bug')} />
                                <FilterChip label="🟡 Improvements" active={catFilter === 'improvement'} onClick={() => setCatFilter('improvement')} />
                                <FilterChip label="🟢 Features" active={catFilter === 'feature_request'} onClick={() => setCatFilter('feature_request')} />
                            </div>
                            <div className="flex gap-2 mb-5 flex-wrap">
                                <span className="text-xs text-slate-400 self-center mr-1">Status:</span>
                                <FilterChip label="All" active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} />
                                <FilterChip label="Open" active={statusFilter === 'open'} onClick={() => setStatusFilter('open')} />
                                <FilterChip label="In Progress" active={statusFilter === 'in_progress'} onClick={() => setStatusFilter('in_progress')} />
                                <FilterChip label="Resolved" active={statusFilter === 'resolved'} onClick={() => setStatusFilter('resolved')} />
                            </div>

                            {filtered.length === 0 ? (
                                <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
                                    <MessageSquare size={40} className="mx-auto text-slate-300 mb-3" />
                                    <p className="text-slate-500">No feedback matches your filters</p>
                                    <p className="text-sm text-slate-400 mt-1">Try broadening your filter selection</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {filtered.map(item => {
                                        const cat = CATEGORY_META[item.category] || CATEGORY_META.bug;
                                        const st = STATUS_META[item.status] || STATUS_META.open;
                                        const isExpanded = expandedId === item.id;

                                        return (
                                            <div key={item.id} className={`bg-white rounded-xl border transition-colors ${
                                                isExpanded ? 'border-amber-200 shadow-sm' : 'border-slate-100 hover:border-slate-200'
                                            }`}>
                                                {/* Summary row */}
                                                <button
                                                    onClick={() => toggleExpand(item.id, item)}
                                                    className="w-full px-4 py-3 flex items-center gap-3 text-left"
                                                >
                                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${cat.color}`}>
                                                        {cat.emoji} {cat.label}
                                                    </span>
                                                    <span className="flex-1 text-sm text-slate-700 truncate">{item.message}</span>
                                                    <span className="text-xs text-slate-400 whitespace-nowrap">
                                                        {item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}
                                                    </span>
                                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.color}`}>
                                                        {st.label}
                                                    </span>
                                                    {item.admin_reply && (
                                                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-600 font-medium">
                                                            Replied
                                                        </span>
                                                    )}
                                                    {isExpanded ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                                                </button>

                                                {/* Expanded detail */}
                                                {isExpanded && (
                                                    <div className="px-4 pb-4 pt-1 border-t border-slate-100 space-y-4">
                                                        {/* Full message */}
                                                        <div>
                                                            <label className="block text-xs font-medium text-slate-500 mb-1">Full Message</label>
                                                            <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 leading-relaxed">
                                                                {item.message}
                                                            </p>
                                                        </div>

                                                        {/* Submitter + date row */}
                                                        <div className="flex items-center gap-4 text-xs text-slate-400 flex-wrap">
                                                            <span>From: <span className="text-slate-600 font-medium">{item.user_email || 'unknown'}</span></span>
                                                            <span>Submitted: {item.created_at ? new Date(item.created_at).toLocaleString() : '—'}</span>
                                                            {item.resolved_at && <span>Resolved: {new Date(item.resolved_at).toLocaleString()}</span>}
                                                            {item.admin_reply_at && (
                                                                <span className="text-indigo-500">
                                                                    Replied: {new Date(item.admin_reply_at).toLocaleString()} by {item.replied_by || 'admin'}
                                                                </span>
                                                            )}
                                                            {item.email_reply_sent && (
                                                                <span className="text-emerald-500 flex items-center gap-1">
                                                                    <CheckCircle size={10} /> Email sent
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Status update */}
                                                        <div>
                                                            <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
                                                            <div className="flex gap-2">
                                                                {Object.entries(STATUS_META).map(([key, meta]) => (
                                                                    <button
                                                                        key={key}
                                                                        onClick={() => setEditStatus(prev => ({ ...prev, [item.id]: key }))}
                                                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                                                                            (editStatus[item.id] || item.status) === key
                                                                                ? meta.color + ' border-current'
                                                                                : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                                                                        }`}
                                                                    >
                                                                        {meta.label}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {/* Internal notes */}
                                                        <div>
                                                            <label className="block text-xs font-medium text-slate-500 mb-1">Internal Notes (admin only — never shown to teachers)</label>
                                                            <textarea
                                                                value={editNotes[item.id] ?? item.internal_notes ?? ''}
                                                                onChange={e => setEditNotes(prev => ({ ...prev, [item.id]: e.target.value }))}
                                                                placeholder="Add notes for the team..."
                                                                rows={2}
                                                                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-vertical"
                                                            />
                                                        </div>

                                                        {/* Admin Reply */}
                                                        <div>
                                                            <label className="block text-xs font-medium text-slate-500 mb-1">
                                                                Admin Reply <span className="text-slate-400 font-normal">(visible to the teacher)</span>
                                                            </label>
                                                            <textarea
                                                                value={editReply[item.id] ?? item.admin_reply ?? ''}
                                                                onChange={e => setEditReply(prev => ({ ...prev, [item.id]: e.target.value }))}
                                                                placeholder="Write a reply to the teacher..."
                                                                rows={3}
                                                                className="w-full px-3 py-2 rounded-lg border border-indigo-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-vertical bg-indigo-50/30"
                                                            />
                                                            {/* Email opt-in */}
                                                            {(editReply[item.id] || '').trim() && (
                                                                <label className="flex items-center gap-2 mt-2 text-xs text-slate-600 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!editSendEmail[item.id]}
                                                                        onChange={e => setEditSendEmail(prev => ({ ...prev, [item.id]: e.target.checked }))}
                                                                        className="rounded border-slate-300"
                                                                    />
                                                                    Also send this reply as an email to {item.user_email || 'the submitter'}
                                                                </label>
                                                            )}
                                                        </div>

                                                        {/* Save result feedback */}
                                                        {saveResult?.id === item.id && (
                                                            <div className={`flex items-center gap-2 p-2 rounded-lg text-xs font-medium ${
                                                                saveResult.success
                                                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                                    : 'bg-amber-50 text-amber-700 border border-amber-200'
                                                            }`}>
                                                                {saveResult.success ? (
                                                                    <>
                                                                        <CheckCircle size={14} />
                                                                        Saved successfully{saveResult.emailSent ? ' · Email sent ✓' : ''}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Activity size={14} />
                                                                        Partial save: {saveResult.errors.join('; ')}
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Save */}
                                                        <div className="flex justify-end">
                                                            <button
                                                                onClick={() => handleSave(item.id)}
                                                                disabled={saving === item.id}
                                                                className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 disabled:opacity-50 transition-colors"
                                                            >
                                                                {saving === item.id ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                                                                {saving === item.id ? 'Saving...' : 'Save All Changes'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}

                    {/* ── WEEKLY PULSE SUB-TAB ── */}
                    {subTab === 'weekly' && (
                        weeklyItems.length === 0 ? (
                            <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
                                <MessageSquare size={40} className="mx-auto text-slate-300 mb-3" />
                                <p className="text-slate-500">No weekly feedback submitted yet</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {weeklyItems.map(item => (
                                    <div key={item.id} className="bg-white rounded-xl border border-slate-100 p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-slate-700">
                                                {item.user_email || item.user_uuid}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-slate-400">
                                                    Week {item.iso_week}, {item.iso_year}
                                                </span>
                                                <span className="text-xs text-slate-400">
                                                    {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                            <div className="bg-slate-50 rounded-lg p-2">
                                                <span className="text-slate-400 block mb-0.5">Hours Saved</span>
                                                <span className="font-semibold text-slate-700">{item.hours_saved ?? '—'}</span>
                                            </div>
                                            <div className="bg-slate-50 rounded-lg p-2">
                                                <span className="text-slate-400 block mb-0.5">Most Used Feature</span>
                                                <span className="font-semibold text-slate-700">{item.most_used_feature || '—'}</span>
                                            </div>
                                            <div className="bg-slate-50 rounded-lg p-2">
                                                <span className="text-slate-400 block mb-0.5">Disappointed if Gone?</span>
                                                <span className={`font-semibold ${item.disappointed_if_gone ? 'text-emerald-600' : 'text-slate-500'}`}>
                                                    {item.disappointed_if_gone === true ? 'Yes' : item.disappointed_if_gone === false ? 'No' : '—'}
                                                </span>
                                            </div>
                                            <div className="bg-slate-50 rounded-lg p-2">
                                                <span className="text-slate-400 block mb-0.5">Confusion Point</span>
                                                <span className="font-semibold text-slate-700">{item.confusion_point || '—'}</span>
                                            </div>
                                        </div>
                                        {item.suggestion_text && (
                                            <div className="mt-2 p-2 bg-amber-50 rounded-lg text-xs text-amber-800">
                                                <span className="font-medium">Suggestion:</span> {item.suggestion_text}
                                            </div>
                                        )}
                                        {item.additional_comments && (
                                            <div className="mt-2 p-2 bg-blue-50 rounded-lg text-xs text-blue-800">
                                                <span className="font-medium">Additional:</span> {item.additional_comments}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )
                    )}

                    {/* ── UPDATE REPORTS SUB-TAB ── */}
                    {subTab === 'updates' && (
                        updateItems.length === 0 ? (
                            <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
                                <MessageSquare size={40} className="mx-auto text-slate-300 mb-3" />
                                <p className="text-slate-500">No update feedback submitted yet</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {updateItems.map(item => (
                                    <div key={item.id} className="bg-white rounded-xl border border-slate-100 p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-slate-700">
                                                {item.user_email || item.user_uuid}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                {item.deployment_version && (
                                                    <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full font-medium">
                                                        {item.deployment_version}
                                                    </span>
                                                )}
                                                <span className="text-xs text-slate-400">
                                                    {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                                            <div className="bg-slate-50 rounded-lg p-2">
                                                <span className="text-slate-400 block mb-0.5">Feature Area</span>
                                                <span className="font-semibold text-slate-700">{item.feature_area || '—'}</span>
                                            </div>
                                            <div className="bg-slate-50 rounded-lg p-2">
                                                <span className="text-slate-400 block mb-0.5">Worked as Expected?</span>
                                                <span className={`font-semibold ${item.worked_as_expected ? 'text-emerald-600' : 'text-red-600'}`}>
                                                    {item.worked_as_expected === true ? 'Yes' : item.worked_as_expected === false ? 'No' : '—'}
                                                </span>
                                            </div>
                                            <div className="bg-slate-50 rounded-lg p-2">
                                                <span className="text-slate-400 block mb-0.5">Friction Level</span>
                                                <span className="font-semibold text-slate-700">
                                                    {item.friction_level ? `${item.friction_level}/5` : '—'}
                                                    {item.friction_level >= 4 && ' ⚠️'}
                                                </span>
                                            </div>
                                        </div>
                                        {item.issue_description && (
                                            <div className="mt-2 p-2 bg-red-50 rounded-lg text-xs text-red-800">
                                                <span className="font-medium">Issue:</span> {item.issue_description}
                                            </div>
                                        )}
                                        {item.additional_comments && (
                                            <div className="mt-2 p-2 bg-blue-50 rounded-lg text-xs text-blue-800">
                                                <span className="font-medium">Additional:</span> {item.additional_comments}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </>
            )}
        </motion.div>
    );
};


export default AdminDashboard;

